/**
 * Clinical reference ranges for the tracking graphs.
 *
 * These describe healthy / target zones so the charts are meaningful from a
 * clinical and patient-facing point of view (rather than just plotting raw
 * numbers with no context). They are unit-aware and pulled from the same
 * thresholds already used elsewhere in the app.
 *
 * Returns { bands: [...], lines: [...] } where:
 *   - band:  { from, to, color, label }  -> a shaded target zone
 *   - line:  { value, color, label }     -> a single threshold marker
 *
 * Anything that has no established, unit-safe range simply returns empty
 * arrays so no misleading guidelines are drawn.
 */

const GREEN = '#5FB737';
const GREEN_FILL = 'rgba(95, 183, 55, 0.10)';
const AMBER = '#FCA039';

/**
 * @param {string} type   tracking graph type (e.g. 'blood-glucose')
 * @param {object} store  vuex store (for the user's chosen units)
 * @returns {{bands: Array, lines: Array}}
 */
export default function clinicalRanges(type, store) {
  const empty = { bands: [], lines: [] };
  const settings = (store && store.state && store.state.user && store.state.user.settings) || {};
  const bgMeasure = (settings.blood_glucose && settings.blood_glucose.measure) || 'mmoll';
  const hba1cMeasure = (settings.hba1c && settings.hba1c.measure) || 'mmol-mol';

  switch (type) {
    case 'blood-glucose':
      // General target range for good control.
      return bgMeasure === 'mgdl'
        ? { bands: [{ from: 72, to: 140, color: GREEN_FILL, label: 'Target range' }], lines: [] }
        : { bands: [{ from: 4.0, to: 7.8, color: GREEN_FILL, label: 'Target range' }], lines: [] };

    case 'ketones':
      // Nutritional ketosis (blood ketones, mmol/L).
      if (bgMeasure === 'mgdl') return empty;
      return { bands: [{ from: 0.5, to: 3.0, color: GREEN_FILL, label: 'Nutritional ketosis' }], lines: [] };

    case 'hba1c':
      return hba1cMeasure === 'percent'
        ? {
            bands: [],
            lines: [
              { value: 5.7, color: AMBER, label: 'Prediabetes' },
              { value: 6.5, color: GREEN, label: 'Diabetes threshold' }
            ]
          }
        : {
            bands: [],
            lines: [
              { value: 42, color: AMBER, label: 'Prediabetes' },
              { value: 48, color: GREEN, label: 'Diabetes threshold' }
            ]
          };

    case 'cholesterol':
      // Total cholesterol target.
      return bgMeasure === 'mgdl'
        ? { bands: [], lines: [{ value: 193, color: GREEN, label: 'Target < 193 mg/dL' }] }
        : { bands: [], lines: [{ value: 5.0, color: GREEN, label: 'Target < 5 mmol/L' }] };

    case 'blood-pressure':
      // mmHg thresholds are unit-independent.
      return {
        bands: [],
        lines: [
          { value: 140, color: AMBER, label: 'High systolic' },
          { value: 90, color: AMBER, label: 'High diastolic' }
        ]
      };

    default:
      return empty;
  }
}



// WEBPACK FOOTER //
// ./src/helpers/GraphManager/clinicalRanges.js